import { typesetCatalog } from '@fixit/shared/utils/czech-typography';
import { createContext, type ReactNode, useContext, useMemo } from 'react';
import { csCZ } from './cs-CZ';
import { useLocale } from './locale';

type Dict = Record<string, string>;
type Vars = Record<string, string | number>;

// Czech chrome typeset once at load: one-letter prepositions bound to the next
// word with U+00A0 (`typesetCatalog`), before interpolation, so values are never altered.
const CS_TYPESET: Dict = typesetCatalog(csCZ, 'cs');

const I18nContext = createContext<Dict>(CS_TYPESET);

/** English-as-key dictionaries: en-US needs no entries — the key IS the English string. */
const EMPTY_DICT: Dict = {};

function interpolate(template: string, vars?: Vars): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (_, k: string) => (k in vars ? String(vars[k]) : `{${k}}`));
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const locale = useLocale();
  const dict = locale === 'en-US' ? EMPTY_DICT : CS_TYPESET;
  return <I18nContext.Provider value={dict}>{children}</I18nContext.Provider>;
}

/** English-as-key translator: t('Start test') → 'Spustit test', falling back to the key itself. */
export function useT(): (key: string, vars?: Vars) => string {
  const dict = useContext(I18nContext);
  return useMemo(() => (key: string, vars?: Vars) => interpolate(dict[key] ?? key, vars), [dict]);
}
